/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useState} from 'react';
import isEmpty from 'lodash.isempty';
import {error} from '@ion/core/src/logger/logger';
import {Device, GenericObject, JobConflictStatus} from '@ion/core/src/types/entities';
import {
  extractJobIdsFromDevices,
  fetchPaginatedJobDetails,
  fetchPaginatedResults,
  formatDeviceQueryString,
  formatTransactionStatus,
  parseTasks,
  transformConflictResult
} from './JobConflictUtils';

interface JobConflictResolverProps {
  accountId: number;
  devices: Device[];
  tasks?: GenericObject;
  searchEntireAccount?: boolean;
}

export type useGetJobConflictsResult = {
  conflicts: GenericObject[];
  isConflictsLoading: boolean;
  conflictsError: boolean;
  showConflicts: boolean;
  devicesInConflict: Device[];
  setShowConflicts: (show: boolean) => void;
  getDeviceConflicts: () => Promise<GenericObject[]>;
};

const useGetJobConflicts = ({
  accountId,
  devices,
  tasks,
  searchEntireAccount = false
}: JobConflictResolverProps): useGetJobConflictsResult => {
  const [conflicts, setConflicts]                   = useState<GenericObject[]>([]);
  const [isConflictsLoading, setIsConflictsLoading] = useState<boolean>(false);
  const [conflictsError, setConflictsError]         = useState<boolean>(false);
  const [showConflicts, setShowConflicts]           = useState<boolean>(false);
  const [devicesInConflict, setDevicesInConflict]   = useState<Device[]>([]);

  const validTransactionStatuses = formatTransactionStatus(Object?.values(JobConflictStatus) || []);

  const getDeviceConflicts = useCallback(async(): Promise<GenericObject[]> => {
    setIsConflictsLoading(true);
    setConflictsError(false);
  
    try {
      const parsedTasks            = parseTasks(tasks);
      const platformIds            = parsedTasks.map(task => task.platformId);
      const formattedDevicesString = formatDeviceQueryString(devices);
  
      const conflictResults = await Promise.all(
        platformIds.map(async(platformId) => {
          const allResults = await fetchPaginatedResults({
            accountId,
            devices           : formattedDevicesString,
            transactionStatus : validTransactionStatuses,
            platformId,
            searchEntireAccount
          });
  
          if (isEmpty(allResults)) {
            return {
              allResults : [],
              jobDetails : [],
              devices    : []
            };
          }
  
          const jobIds     = extractJobIdsFromDevices(allResults);
          const jobDetails = await fetchPaginatedJobDetails(jobIds, accountId);
  
          const resultsWithPlatformId = allResults.map(result => ({
            ...result,
            platformId
          }));

          const devices = resultsWithPlatformId?.map(result => result?.device);
  
          return {
            allResults : resultsWithPlatformId,
            jobDetails,
            devices
          };
        })
      );
  
      const mergedResults    = [];
      const mergedJobDetails = [];
      const mergedDevicesMap = new Map();
  
      conflictResults?.forEach(res => {
        mergedResults.push(...res.allResults);
        mergedJobDetails.push(...res.jobDetails);
        res.devices.forEach(device => {
          if (device?.esn && !mergedDevicesMap.has(device.esn)) {
            mergedDevicesMap.set(device.esn, device);
          }
        });
      });

      const uniqueDevices = Array.from(mergedDevicesMap.values());
  
      setDevicesInConflict(uniqueDevices);
  
      if (mergedResults.length === 0) {
        setConflicts([]);
        setShowConflicts(false);
        return [];
      }
  
      const deviceResults = await transformConflictResult(mergedResults, mergedJobDetails, parsedTasks);
  
      setConflicts(deviceResults);
      setShowConflicts(deviceResults.length > 0);
      return deviceResults;
    } catch (err) {
      error('Failed to get device conflicts:', err);
      setConflictsError(true);
      return [];
    } finally {
      setIsConflictsLoading(false);
    }
  }, [accountId, devices, tasks, searchEntireAccount]);

  return {
    conflicts,
    devicesInConflict,
    isConflictsLoading,
    conflictsError,
    setShowConflicts,
    showConflicts,
    getDeviceConflicts
  };
};

export default useGetJobConflicts;
